<script setup lang="ts">
import type {
  RouteLocationRaw,
} from 'vue-router';
import { computed, toRef } from 'vue';
import {
  RouterLink,
} from 'vue-router';

import { ANONYMOUS_MAIN_ROUTE_NAME } from '@modules/anonymous-main/constants';
import { useConfig } from '@modules/config/composables';
import { useLogoStore } from '@modules/config/store';
import {
  ADMIN_DASHBOARD_ROUTE_NAME,
  AFFILIATE_DASHBOARD_ROUTE_NAME,
} from '@modules/dashboard/constants';
import { useAddMerchantIdToRoute } from '@modules/merchant/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';

import type { VLogoProps } from './types';

const props = withDefaults(defineProps<VLogoProps>(), {
  type: 'logo',
});

const regExpUrl = /^https?:\/\/\S+$/;

const { config } = useConfig();
const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();
const logoHeaderUrl = toRef(useLogoStore(), 'logoHeaderUrl');
const { addMerchantId } = useAddMerchantIdToRoute();

const logoLink = computed<RouteLocationRaw>(() => {
  if (isAdminNamespace.value) {
    return addMerchantId({
      name: ADMIN_DASHBOARD_ROUTE_NAME,
    });
  }

  if (isAffiliateNamespace.value) {
    return addMerchantId({
      name: AFFILIATE_DASHBOARD_ROUTE_NAME,
    });
  }

  return {
    name: ANONYMOUS_MAIN_ROUTE_NAME,
  };
});

const logoUrl = computed(() => (props.type === 'logo_mini' && logoHeaderUrl.value) || config.value?.[props.type]);

const isExternalRedirect = computed<boolean | undefined>(() => (
  props.type === 'logo'
  && config.value?.welcome_page_redirects?.enable
  && regExpUrl.test(config.value?.welcome_page_redirects?.url)
));

const linkComponent = computed(() => (isExternalRedirect.value ? 'a' : RouterLink));

const linkProps = computed(() => (
  isExternalRedirect.value
    ? { href: config.value?.welcome_page_redirects?.url }
    : { to: logoLink.value }
));

function setDefaultLogo(event: Event): void {
  const image = event.target as HTMLImageElement;

  if (props.type === 'logo_mini' && !image.src.includes('logo-header.png')) {
    image.src = '/logo-header.png';
  }

  if (props.type === 'logo' && !image.src.includes('logo-main.png')) {
    image.src = '/logo-main.png';
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'VLogo'" class="flex h-full items-center justify-center">
    <component
      :is="linkComponent"
      v-bind="linkProps"
      class="h-full"
    >
      <span class="flex h-full items-center">
        <img
          v-if="logoUrl"
          :src="logoUrl"
          alt="logo"
          class="max-h-full"
          @error="setDefaultLogo"
        >
      </span>
    </component>
  </div>
</template>
